import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
    Box,
    Collapse,
    IconButton,
    Stack,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import { TABLE_ROW_COUNT_OPTIONS } from '../../../helpers/constants';
import { handleChangeRowsPerPage, handlePageChange } from '../../../helpers/handleTableFunctions';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import BookingCard from './BookingCard';

const COLUMN_COUNT = 6;

const CARD_MIN_WIDTH = 320;
const CARD_GAP = 16;

const ExpandedBookings = ({ customer }) => {
    const { t } = useTranslation();
    const [activeBatch, setActiveBatch] = useState(0);
    const [batchSize, setBatchSize] = useState(1);
    const containerRef = useRef(null);

    const bookings = customer?.bookings || [];
    const batchCount = Math.ceil(bookings.length / batchSize);
    const visibleBookings = bookings.slice(
        activeBatch * batchSize,
        activeBatch * batchSize + batchSize,
    );

    useEffect(() => {
        setActiveBatch(0);
    }, [customer?.key]);

    useLayoutEffect(() => {
        if (containerRef.current) {
            const width = containerRef.current.offsetWidth;
            const count = Math.max(1, Math.floor((width + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP)));
            setBatchSize(count);
        }
    }, [customer?.key]);

    if (bookings.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {t('crm.noBookings')}
            </Typography>
        );
    }

    return (
        <Box sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
            >
                <Typography variant="subtitle2">
                    {t('crm.bookingsCount', { count: bookings.length })}
                </Typography>
                {batchCount > 1 && (
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title={t('common.previous')}>
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={activeBatch === 0}
                                    onClick={() => setActiveBatch((p) => p - 1)}
                                >
                                    <ChevronLeftIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t('common.next')}>
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={activeBatch >= batchCount - 1}
                                    onClick={() => setActiveBatch((p) => p + 1)}
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                )}
            </Stack>

            <Box
                ref={containerRef}
                sx={{
                    display: 'flex',
                    gap: 2,
                    pb: 1,
                }}
            >
                {visibleBookings.map((booking) => (
                    <BookingCard key={booking.id} booking={booking} />
                ))}
            </Box>

            {batchCount > 1 && (
                <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ mt: 1 }}>
                    {Array.from({ length: batchCount }, (_, idx) => (
                        <Box
                            key={idx}
                            onClick={() => setActiveBatch(idx)}
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                backgroundColor:
                                    idx === activeBatch ? 'primary.main' : 'action.disabled',
                            }}
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
};

const CustomersTable = ({ customers, loading, page, limit, setParams, total }) => {
    const { t } = useTranslation();
    const [expandedKey, setExpandedKey] = useState(null);

    const pageStart = page * limit;
    const visible = customers.slice(pageStart, pageStart + limit);

    const toggle = (key) => {
        setExpandedKey((prev) => (prev === key ? null : key));
    };

    return (
        <Box>
            <TableContainer>
                <Table sx={{ minWidth: 700 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 48 }} />
                            <TableCell>{t('crm.columns.name')}</TableCell>
                            <TableCell>{t('crm.columns.email')}</TableCell>
                            <TableCell>{t('crm.columns.phone')}</TableCell>
                            <TableCell>{t('crm.columns.bookingCount')}</TableCell>
                            <TableCell>{t('crm.columns.lastBooking')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <SkeletonTable rows={limit} cells={COLUMN_COUNT} />
                        ) : (
                            visible.map((customer) => {
                                const last = customer.lastBooking;
                                const isOpen = expandedKey === customer.key;
                                return (
                                    <React.Fragment key={customer.key}>
                                        <TableRow
                                            hover
                                            onClick={() => toggle(customer.key)}
                                            sx={{
                                                cursor: 'pointer',
                                                '& > *': { borderBottom: 'unset' },
                                            }}
                                        >
                                            <TableCell sx={{ width: 48 }}>
                                                <IconButton size="small">
                                                    {isOpen ? (
                                                        <KeyboardArrowDownIcon />
                                                    ) : (
                                                        <KeyboardArrowRightIcon />
                                                    )}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                {`${customer.firstName} ${customer.lastName}`.trim() ||
                                                    t('crm.unknownName')}
                                            </TableCell>
                                            <TableCell>{customer.email || '-'}</TableCell>
                                            <TableCell>{customer.phone || '-'}</TableCell>
                                            <TableCell>{customer.bookingCount}</TableCell>
                                            <TableCell>
                                                {last?.timeslot?.startTime
                                                    ? formatDateTimeInVenueTz(
                                                          last.timeslot.startTime,
                                                      )
                                                    : '-'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell
                                                colSpan={COLUMN_COUNT}
                                                sx={{
                                                    py: 0,
                                                    borderBottom: isOpen ? undefined : 'none',
                                                    bgcolor: 'background.default',
                                                }}
                                            >
                                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                    <ExpandedBookings customer={customer} />
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                count={total || 0}
                rowsPerPage={limit}
                component="div"
                page={visible.length === 0 ? 0 : page}
                onPageChange={(_, newPage) => handlePageChange(newPage, setParams)}
                rowsPerPageOptions={TABLE_ROW_COUNT_OPTIONS}
                onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setParams)}
                labelRowsPerPage={t('labelRowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
        </Box>
    );
};

export default CustomersTable;
